import { computed } from '@angular/core';
import { signalStoreFeature, type, withComputed } from '@ngrx/signals';
import { CategoryState } from './category.state';
import { IndicatorTreeNode } from '../../models/IndicatorTreeNode';
import { Category } from '../../models/category';
import { StateFilter } from '../crud.state';

const filterItems = (items: Category[], filter: StateFilter) => {
  return items.filter(item => {
    const titleMatch = item.name
      .toLowerCase()
      .includes(filter.query.toLowerCase());
    const lockedMatch = filter.locked === item.locked || filter.locked === null;
    const categoryMatch =
      (item.parent && filter.category.includes(item.parent)) ||
      filter.category.length === 0;

    // auto-filter children
    return titleMatch && categoryMatch && lockedMatch;
  });
};

export function withCategorySelectors() {
  return signalStoreFeature(
    {
      state: type<CategoryState>(),
    },
    withComputed(({ items, filter, selection }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() => Object.values(items())),
      filteredSortedByName: computed(() => {
        let filtered = Object.values(items());
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.sort((a, b) => (a.name < b.name ? -1 : 1));
      }),
      filteredSelection: computed(() => {
        let filtered = selection();
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered as Category[];
      }),
      asTree: computed(() => {
        const nodes: Record<string, IndicatorTreeNode> = Object.keys(
          items()
        ).reduce(
          (acc, key) => {
            const item = items()[key];
            acc[key] = {
              data: {
                _id: item._id || '',
                key: item._id || '',
                label: item.name,
                type: 'category',
              },
              _id: item._id || '',
              key: item._id || '',
              label: item.name,
              locked: item.locked,
              workspaces: item.workspaces || [],
              type: 'category',
              children: [],
              sorting: item.sorting,
              icon: 'fa-regular fa-folder',
            };
            return acc;
          },
          {} as Record<string, IndicatorTreeNode>
        );

        // Placeholder for root items
        let rootItems: IndicatorTreeNode[] = [];

        // Organize items by parent-child relationship
        Object.values(nodes).forEach(item => {
          if (!item._id) return;
          const parentId = items()[item._id].parent;
          if (parentId && parentId in nodes) {
            nodes[parentId].children.push(item);
          } else {
            rootItems.push(item);
          }
        });

        // Sort root items
        rootItems = rootItems.sort((a, b) => a.sorting - b.sorting);

        // Function to recursively sort children
        const sortChildren = (item: IndicatorTreeNode) => {
          if (item.children.length > 0) {
            item.children = item.children.sort((a, b) => a.sorting - b.sorting);
            item.children.forEach(sortChildren);
          }
        };

        // Recursively sort children for each root item
        rootItems.forEach(sortChildren);
        return rootItems;
      }),
      asFlatTree: computed(() => {
        const categories = Object.values(items());
        const rootCategories = categories.filter(category => !category.parent);
        const result: Category[] = [];

        /**
         * Recursively adds a category and its children to the result array,
         * with numbering.
         *
         * @param category - The category to add.
         * @param prefix - The current number prefix.
         */
        function addCategoryAndChildren(category: Category, prefix: string) {
          category.number = prefix;
          result.push(category);
          const children = categories
            .filter(child => child.parent === category._id)
            .sort((a, b) => a.sorting - b.sorting);

          children.forEach((child, index) => {
            addCategoryAndChildren(child, `${prefix}.${index + 1}`);
          });
        }

        rootCategories
          .sort((a, b) => a.sorting - b.sorting)
          .forEach((category, index) =>
            addCategoryAndChildren(category, `${index + 1}`)
          );

        return result;
      }),
    }))
  );
}
